import styled from "styled-components";
import { GetDatacenter_datacenter_tla_installContexts } from "../../views/App/__generated__/GetDatacenter";
import { useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import {
    Check,
    Circle,
    Clear,
    DeleteForeverOutlined,
    EditOutlined,
    FileCopy,
    KeyboardArrowDown,
    KeyboardArrowUp,
} from "@mui/icons-material";
import { copyToClipboard } from "../CopyLabel/CopyLabel";
import { TOAST_SETTINGS } from "../BillingDetails/AddPaymentMethodDialog";
import { toast } from "react-toastify";

const Content = styled.div`
    display: flex;
    flex-direction: column;
    border-radius: 8px;
    padding: 10px 14px;
    border: 1px solid ${(props) => props.theme.lightGrayBottom};
    align-items: center;
    width: 100%;
`;

const Main = styled.div`
    display: flex;
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    cursor: pointer;
`;

const Title = styled.div`
    font-size: 14px;
`;

const Description = styled.div`
    font-size: 13px;
    color: ${(props) => props.theme.gray};
`;

const Details = styled.div`
    border-radius: 8px;
    background-color: ${(props) => props.theme.grayBackground};
    width: 100%;
    display: flex;
    flex-direction: column;
    margin-top: 4px;
    font-size: 13px;
    padding: 10px 14px;
    border: 1px solid ${(props) => props.theme.lightGrayBottom};
`;

const Group = styled.div`
    margin-bottom: 8px;
`;

interface Props {
    datacenterId: string;
    isntallContext: GetDatacenter_datacenter_tla_installContexts;
    onEditClick: () => void;
    onDeleteClick: () => void;
}

export default function TLADatacenterInstallContextsItem({
    isntallContext,
    datacenterId,
    onDeleteClick,
    onEditClick,
}: Props): JSX.Element {
    const [open, setOpen] = useState<boolean>(false);

    const {
        description,
        disableVaultSSH,
        id,
        labels,
        name,
        nodeLabels,
        state,
        command,
        privateCIDR,
        privateId,
        securityToken,
        upgradeDocker,
    } = isntallContext;

    async function copyValue(value: string) {
        try {
            await copyToClipboard(value);
            toast.success("Value copied", TOAST_SETTINGS);
        } catch (e: any) {
            toast.error("Error while copying", TOAST_SETTINGS);
        }
    }

    return (
        <Content>
            <Main onClick={() => setOpen(!open)}>
                <div className="flex flex-row gap-2">
                    <div>
                        <Tooltip title={state}>
                            <Circle
                                style={{
                                    fontSize: "12px",
                                    color: state === "creating" ? "orange" : state === "created" ? "green" : "gray",
                                }}
                            />
                        </Tooltip>
                    </div>
                    <div>
                        <Title>{name}</Title>
                        <Description>{description}</Description>
                    </div>
                </div>
                <div>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onEditClick();
                        }}
                    >
                        <EditOutlined fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDeleteClick();
                        }}
                    >
                        {<DeleteForeverOutlined fontSize="small" />}
                    </IconButton>
                    <IconButton size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUp color="primary" /> : <KeyboardArrowDown color="primary" />}
                    </IconButton>
                </div>
            </Main>
            {open && (
                <Details>
                    {id && (
                        <Group>
                            <strong>ID</strong>
                            <div>{id}</div>
                        </Group>
                    )}
                    {name && (
                        <Group>
                            <strong>Name</strong>
                            <div>{name}</div>
                        </Group>
                    )}
                    {description && (
                        <Group>
                            <strong>Description</strong>
                            <div>{description}</div>
                        </Group>
                    )}
                    {command && (
                        <Group>
                            <strong>Command</strong>
                            <div className="flex flex-row gap-2 items-center">
                                {command}{" "}
                                <Tooltip title="Copy">
                                    <IconButton size="small" onClick={() => copyValue(command)}>
                                        <FileCopy fontSize="small" style={{ color: "black" }} />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        </Group>
                    )}
                    {securityToken && (
                        <Group>
                            <strong>Security token</strong>
                            <div>{securityToken}</div>
                        </Group>
                    )}
                    {privateId && (
                        <Group>
                            <strong>Private ID</strong>
                            <div>{privateId}</div>
                        </Group>
                    )}

                    {privateCIDR && (
                        <Group>
                            <strong>Private CIDR</strong>
                            <div>{privateCIDR}</div>
                        </Group>
                    )}

                    <Group>
                        <strong>Disable Vault SSH?</strong>
                        <div>{disableVaultSSH ? <Check color="success" /> : <Clear color="error" />}</div>
                    </Group>
                    <Group>
                        <strong>Upgrade Docker?</strong>
                        <div>{upgradeDocker ? <Check color="success" /> : <Clear color="error" />}</div>
                    </Group>

                    <Group>
                        <strong>Labels</strong>
                        <ul style={{ margin: 0, padding: 0, paddingLeft: "28px" }}>
                            {labels.map((label, i) => (
                                <li key={`label-${i}`}>{label}</li>
                            ))}
                        </ul>
                    </Group>

                    <Group>
                        <strong>Node labels</strong>
                        <ul style={{ margin: 0, padding: 0, paddingLeft: "28px" }}>
                            {nodeLabels.map((label, i) => (
                                <li key={`label-${i}`}>{label}</li>
                            ))}
                        </ul>
                    </Group>
                </Details>
            )}
        </Content>
    );
}
